import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { 
  X, 
  Calendar, 
  Tag, 
  Users, 
  CheckSquare, 
  MessageSquare,
  Trash2,
  Edit3,
  Plus,
  Flag,
  MoreHorizontal
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/config/api';

export const CardDetailModal = ({
  isOpen,
  onClose,
  card,
  projectLabels,
  projectMembers,
  onUpdate,
  onDelete,
  projectId,
  token
}) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [title, setTitle] = useState(card?.title || '');
  const [description, setDescription] = useState(card?.description || '');
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [newChecklistTitle, setNewChecklistTitle] = useState('');
  const [isAddingChecklist, setIsAddingChecklist] = useState(false);

  useEffect(() => {
    if (card) {
      setTitle(card.title);
      setDescription(card.description || '');
      fetchComments();
    }
  }, [card?.id]);

  const fetchComments = async () => {
    if (!card?.id) return;
    setIsLoadingComments(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/projects/${projectId}/cards/${card.id}/comments`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setComments(data);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setIsLoadingComments(false);
    }
  };

  const handleTitleSave = () => {
    if (title.trim() && title !== card.title) {
      onUpdate(card.id, { title: title.trim() });
    }
    setIsEditingTitle(false);
  };

  const handleDescriptionSave = () => {
    if (description !== card.description) {
      onUpdate(card.id, { description });
    }
    setIsEditingDescription(false);
  };

  const handleLabelToggle = (labelId) => {
    const currentLabels = card.labels?.map(l => l.id) || [];
    const newLabels = currentLabels.includes(labelId)
      ? currentLabels.filter(id => id !== labelId)
      : [...currentLabels, labelId];
    onUpdate(card.id, { labels: newLabels });
  };

  const handleAssigneeToggle = (userId) => {
    const currentAssignees = card.assignees?.map(a => a.id) || [];
    const newAssignees = currentAssignees.includes(userId)
      ? currentAssignees.filter(id => id !== userId)
      : [...currentAssignees, userId];
    onUpdate(card.id, { assignees: newAssignees });
  };

  const handleDueDateChange = (date) => {
    onUpdate(card.id, { due_date: date ? new Date(date).toISOString() : null });
  };

  const handlePriorityChange = (priority) => {
    onUpdate(card.id, { priority });
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/projects/${projectId}/cards/${card.id}/comments`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ content: newComment })
        }
      );
      
      if (response.ok) {
        const comment = await response.json();
        setComments(prev => [...prev, comment]);
        setNewComment('');
        toast.success('Comment added');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    }
  };

  const handleAddChecklist = async () => {
    if (!newChecklistTitle.trim()) return;
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/projects/${projectId}/cards/${card.id}/checklists`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ title: newChecklistTitle })
        }
      );
      
      if (response.ok) {
        const checklist = await response.json();
        onUpdate(card.id, { 
          checklists: [...(card.checklists || []), checklist] 
        });
        setNewChecklistTitle('');
        setIsAddingChecklist(false);
        toast.success('Checklist added');
      }
    } catch (error) {
      console.error('Error adding checklist:', error);
      toast.error('Failed to add checklist');
    }
  };

  const handleToggleChecklistItem = async (checklistId, itemId) => {
    try {
      await fetch(
        `${BACKEND_URL}/api/projects/${projectId}/cards/${card.id}/checklists/${checklistId}/items/${itemId}`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      // Update locally
      const updatedChecklists = card.checklists.map(cl => {
        if (cl.id === checklistId) {
          return {
            ...cl,
            items: cl.items.map(item => 
              item.id === itemId 
                ? { ...item, completed: !item.completed }
                : item
            )
          };
        }
        return cl;
      });
      
      onUpdate(card.id, { checklists: updatedChecklists });
    } catch (error) {
      console.error('Error toggling item:', error);
    }
  };

  const priorityOptions = [
    { value: 'low', label: 'Low', color: 'bg-gray-500' },
    { value: 'medium', label: 'Medium', color: 'bg-blue-500' },
    { value: 'high', label: 'High', color: 'bg-orange-500' },
    { value: 'urgent', label: 'Urgent', color: 'bg-red-500' }
  ];

  if (!card) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0F2942] border-white/20 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pr-10">
          {isEditingTitle ? (
            <Input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={handleTitleSave}
              onKeyDown={(e) => e.key === 'Enter' && handleTitleSave()}
              className="bg-white/10 border-white/20 text-white text-lg font-semibold"
            />
          ) : (
            <DialogTitle 
              className="text-xl cursor-pointer hover:bg-white/5 p-1 -m-1 rounded"
              onClick={() => setIsEditingTitle(true)}
            >
              {card.title}
            </DialogTitle>
          )}
        </DialogHeader>

        <div className="grid grid-cols-3 gap-6 mt-4">
          {/* Main Content - Left Column */}
          <div className="col-span-2 space-y-6">
            {/* Labels */}
            {card.labels && card.labels.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {card.labels.map(label => (
                  <span
                    key={label.id}
                    className="px-3 py-1 rounded-full text-sm font-medium text-white"
                    style={{ backgroundColor: label.color }}
                  >
                    {label.name}
                  </span>
                ))}
              </div>
            )}

            {/* Description */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Edit3 size={16} className="text-gray-400" />
                <h3 className="text-sm font-medium text-gray-300">Description</h3>
              </div>
              
              {isEditingDescription ? (
                <div>
                  <Textarea
                    autoFocus
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Add a more detailed description... (Markdown supported)"
                    className="bg-white/10 border-white/20 text-white min-h-[150px] resize-none"
                    rows={6}
                  />
                  <div className="flex gap-2 mt-2">
                    <Button
                      size="sm"
                      onClick={handleDescriptionSave}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setDescription(card.description || '');
                        setIsEditingDescription(false);
                      }}
                      className="text-gray-400 hover:text-white"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => setIsEditingDescription(true)}
                  className="bg-white/5 rounded-lg p-4 min-h-[100px] cursor-pointer hover:bg-white/10 transition-colors"
                >
                  {description ? (
                    <div className="prose prose-invert prose-sm max-w-none">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {description}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-gray-500 italic">Add a description...</p>
                  )}
                </div>
              )}
            </div>

            {/* Checklists */}
            {card.checklists && card.checklists.length > 0 && (
              <div className="space-y-4">
                {card.checklists.map(checklist => {
                  const completed = checklist.items?.filter(i => i.completed).length || 0;
                  const total = checklist.items?.length || 0;
                  const progress = total > 0 ? (completed / total) * 100 : 0;
                  
                  return (
                    <div key={checklist.id}>
                      <div className="flex items-center gap-2 mb-2">
                        <CheckSquare size={16} className="text-gray-400" />
                        <h3 className="text-sm font-medium text-gray-300">{checklist.title}</h3>
                        <span className="text-xs text-gray-500">{completed}/{total}</span>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="h-1.5 bg-white/10 rounded-full mb-3">
                        <div 
                          className="h-full bg-green-500 rounded-full transition-all"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      
                      {/* Items */}
                      <div className="space-y-2">
                        {checklist.items?.map(item => (
                          <div key={item.id} className="flex items-center gap-3">
                            <Checkbox
                              checked={item.completed}
                              onCheckedChange={() => handleToggleChecklistItem(checklist.id, item.id)}
                              className="border-white/30 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                            />
                            <span className={`text-sm ${item.completed ? 'text-gray-500 line-through' : 'text-gray-300'}`}>
                              {item.text}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Comments */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare size={16} className="text-gray-400" />
                <h3 className="text-sm font-medium text-gray-300">Comments</h3>
              </div>
              
              {/* Add Comment */}
              <div className="flex gap-3 mb-4">
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Write a comment... (Markdown supported)"
                  className="bg-white/10 border-white/20 text-white resize-none flex-1"
                  rows={2}
                />
                <Button
                  onClick={handleAddComment}
                  disabled={!newComment.trim()}
                  className="bg-blue-600 hover:bg-blue-700 self-end"
                >
                  Post
                </Button>
              </div>
              
              {/* Comments List */}
              <div className="space-y-4">
                {comments.map(comment => (
                  <div key={comment.id} className="flex gap-3">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0"
                      style={{ backgroundColor: comment.user_avatar_color }}
                    >
                      {comment.user_name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-white">{comment.user_name}</span>
                        <span className="text-xs text-gray-500">
                          {format(new Date(comment.created_at), 'MMM d, h:mm a')}
                        </span>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3 prose prose-invert prose-sm max-w-none">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {comment.content}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </div>
                ))}
                
                {isLoadingComments && (
                  <div className="text-center text-gray-500 py-4">Loading comments...</div>
                )}
                
                {!isLoadingComments && comments.length === 0 && (
                  <div className="text-center text-gray-500 py-4">No comments yet</div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar - Right Column */}
          <div className="space-y-4">
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Add to card</div>
            
            {/* Labels */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-full justify-start text-gray-300 hover:bg-white/10 hover:text-white">
                  <Tag size={16} className="mr-2" />
                  Labels
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#1a1d21] border-white/20 w-56">
                {projectLabels.map(label => (
                  <DropdownMenuCheckboxItem
                    key={label.id}
                    checked={card.labels?.some(l => l.id === label.id)}
                    onCheckedChange={() => handleLabelToggle(label.id)}
                    className="text-gray-300 focus:bg-white/10"
                  >
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: label.color }}
                      />
                      {label.name}
                    </div>
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Members */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-full justify-start text-gray-300 hover:bg-white/10 hover:text-white">
                  <Users size={16} className="mr-2" />
                  Members
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#1a1d21] border-white/20 w-56">
                {projectMembers.map(member => (
                  <DropdownMenuCheckboxItem
                    key={member.user_id}
                    checked={card.assignees?.some(a => a.id === member.user_id)}
                    onCheckedChange={() => handleAssigneeToggle(member.user_id)}
                    className="text-gray-300 focus:bg-white/10"
                  >
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs"
                        style={{ backgroundColor: member.avatar_color }}
                      >
                        {member.name?.charAt(0).toUpperCase()}
                      </div>
                      {member.name}
                    </div>
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Checklist */}
            {isAddingChecklist ? (
              <div className="bg-white/5 rounded-lg p-2">
                <Input
                  autoFocus
                  value={newChecklistTitle}
                  onChange={(e) => setNewChecklistTitle(e.target.value)}
                  placeholder="Checklist title"
                  className="bg-transparent border-white/20 text-white text-sm mb-2"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddChecklist()}
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleAddChecklist}
                    disabled={!newChecklistTitle.trim()}
                    className="bg-blue-600 hover:bg-blue-700 text-xs"
                  >
                    Add
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setIsAddingChecklist(false);
                      setNewChecklistTitle('');
                    }}
                    className="text-gray-400 text-xs"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button 
                variant="ghost" 
                className="w-full justify-start text-gray-300 hover:bg-white/10 hover:text-white"
                onClick={() => setIsAddingChecklist(true)}
              >
                <CheckSquare size={16} className="mr-2" />
                Checklist
              </Button>
            )}

            {/* Due Date */}
            <div>
              <Button variant="ghost" className="w-full justify-start text-gray-300 hover:bg-white/10 hover:text-white mb-2">
                <Calendar size={16} className="mr-2" />
                Due Date
              </Button>
              <Input
                type="date"
                value={card.due_date ? format(new Date(card.due_date), 'yyyy-MM-dd') : ''}
                onChange={(e) => handleDueDateChange(e.target.value)}
                className="bg-white/10 border-white/20 text-white text-sm"
              />
            </div>

            {/* Priority */}
            <div>
              <Button variant="ghost" className="w-full justify-start text-gray-300 hover:bg-white/10 hover:text-white mb-2">
                <Flag size={16} className="mr-2" />
                Priority
              </Button>
              <Select value={card.priority} onValueChange={handlePriorityChange}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1d21] border-white/20">
                  {priorityOptions.map(option => (
                    <SelectItem 
                      key={option.value} 
                      value={option.value}
                      className="text-gray-300 focus:bg-white/10"
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${option.color}`} />
                        {option.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="border-t border-white/10 pt-4 mt-4">
              <Button 
                variant="ghost" 
                className="w-full justify-start text-red-400 hover:bg-red-500/10 hover:text-red-300"
                onClick={() => onDelete(card.id)}
              >
                <Trash2 size={16} className="mr-2" />
                Delete Card
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
